'use client';

import { useState } from 'react';
import Turnstile from './Turnstile';
import RequiredAsterisk from './RequiredAsterisk';
import { useSubmitBlogComment } from '@/lib/queries';

interface BlogCommentFormProps {
  blogPostId: number;
  blogPostTitle: string;
  onSuccess?: () => void;
}

export default function BlogCommentForm({
  blogPostId,
  blogPostTitle,
  onSuccess,
}: BlogCommentFormProps) {
  const [formData, setFormData] = useState({
    authorName: '',
    authorEmail: '',
    comment: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileKey, setTurnstileKey] = useState<number>(0);

  const submitComment = useSubmitBlogComment();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      setErrorMessage('Please complete the captcha verification');
      return;
    }

    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await submitComment.mutateAsync({
        blogPostId,
        comment: formData.comment,
        authorName: formData.authorName,
        authorEmail: formData.authorEmail || undefined,
        turnstileToken,
      });
      setSubmitStatus('success');
      setFormData({
        authorName: '',
        authorEmail: '',
        comment: '',
      });
      setTurnstileToken('');
      setTurnstileKey((prev) => prev + 1);
      onSuccess?.();
    } catch (error) {
      console.error('Comment form error:', error);
      setSubmitStatus('error');
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to submit comment. Please try again.');
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
      <h3 className="text-xl font-serif font-bold text-castle-stone-800 mb-4 dark:text-castle-stone-200">
        Leave a comment
      </h3>

      {submitStatus === 'success' ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center dark:bg-green-900/30 dark:border-green-800">
          <div className="text-green-600 text-4xl mb-2">✓</div>
          <h4 className="text-lg font-semibold text-green-800 mb-1">Comment submitted!</h4>
          <p className="text-green-700 text-sm mb-3">
            Thank you for your comment on &ldquo;{blogPostTitle}&rdquo;. It will be visible after
            admin approval.
          </p>
          <button
            onClick={() => {
              setSubmitStatus('idle');
              setTurnstileKey((prev) => prev + 1);
            }}
            className="text-green-600 hover:text-green-700 font-medium underline text-sm"
          >
            Write another comment
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="authorName"
                className="block text-sm font-medium text-castle-stone-700 mb-1 dark:text-castle-stone-300"
              >
                Your name <RequiredAsterisk />
              </label>
              <input
                type="text"
                id="authorName"
                name="authorName"
                value={formData.authorName}
                onChange={handleInputChange}
                required
                minLength={2}
                maxLength={100}
                className="w-full px-3 py-2 border border-castle-stone-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent outline-none transition text-sm"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label
                htmlFor="authorEmail"
                className="block text-sm font-medium text-castle-stone-700 mb-1 dark:text-castle-stone-300"
              >
                Your email{' '}
                <span className="text-castle-stone-500 text-xs dark:text-castle-stone-400">
                  (optional)
                </span>
              </label>
              <input
                type="email"
                id="authorEmail"
                name="authorEmail"
                value={formData.authorEmail}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-castle-stone-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent outline-none transition text-sm"
                placeholder="your.email@example.com"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="comment"
              className="block text-sm font-medium text-castle-stone-700 mb-1 dark:text-castle-stone-300"
            >
              Your comment <RequiredAsterisk />
            </label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleInputChange}
              required
              minLength={1}
              maxLength={2000}
              rows={4}
              className="w-full px-3 py-2 border border-castle-stone-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent outline-none transition resize-none text-sm"
              placeholder="Share your thoughts about this article..."
            />
            <p className="mt-1 text-xs text-castle-stone-500 dark:text-castle-stone-400">
              {formData.comment.length}/2000 characters
            </p>
          </div>

          <div>
            <Turnstile
              key={turnstileKey}
              siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
              onVerify={setTurnstileToken}
            />
          </div>

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitComment.isPending || !turnstileToken}
            className="w-full bg-forest-green-600 hover:bg-forest-green-700 disabled:bg-castle-stone-300 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
          >
            {submitComment.isPending ? 'Submitting...' : 'Submit comment'}
          </button>

          <p className="text-xs text-castle-stone-500 text-center">
            Your comment will be visible after admin approval
          </p>
        </form>
      )}
    </div>
  );
}
